import type { Model } from './codec';
import { featurize, type SparseVec } from './features';
import type { Probs } from './decide';

/** The raw score of every class (intent columns, then domain columns) for one sentence. */
export function scoresOf(model: Model, x: SparseVec): Float32Array {
  const sums = new Float32Array(model.columns);
  for (let at = 0; at < x.index.length; at += 1) {
    const row = x.index[at] ?? 0;
    const value = x.value[at] ?? 0;
    const to = model.rowStart[row + 1] ?? 0;
    for (let entry = model.rowStart[row] ?? 0; entry < to; entry += 1) {
      const column = model.col[entry] ?? 0;
      sums[column] = (sums[column] ?? 0) + value * (model.q[entry] ?? 0);
    }
  }
  return sums.map((sum, column) => (model.bias[column] ?? 0) + sum * (model.scale[column] ?? 1));
}

/** Probabilities over `scores[from..to)`; a higher temperature makes them less sure of themselves. */
export function softmax(scores: ArrayLike<number>, from: number, to: number, temperature = 1): number[] {
  const slice = Array.from({ length: to - from }, (_, at) => (scores[from + at] ?? 0) / temperature);
  const top = Math.max(...slice);
  const exps = slice.map((value) => Math.exp(value - top));
  const total = exps.reduce((sum, value) => sum + value, 0);
  return exps.map((value) => value / total);
}

export type Temperatures = { intent: number; domain: number };

/** What the model thinks of a sentence: a probability for each intent and for each topic. */
export function probsOf(model: Model, text: string, temperatures: Temperatures = { intent: 1, domain: 1 }): Probs {
  const scores = scoresOf(model, featurize(text, model.dim));
  return {
    intent: softmax(scores, 0, model.nIntent, temperatures.intent),
    domain: softmax(scores, model.nIntent, model.columns, temperatures.domain),
  };
}
